import { FormEvent, useEffect, useMemo, useState } from 'react';
import {
  ExternalLink,
  Image,
  Link as LinkIcon,
  Pencil,
  Plus,
  RefreshCw,
  RotateCcw,
  Save,
  SlidersHorizontal,
  ToggleLeft,
  ToggleRight,
  Trash2,
  X,
} from 'lucide-react';
import clsx from 'clsx';
import { extractError } from '@/api/client';
import {
  tiktokPromoApi,
  type TikTokPromoCampaign,
  type TikTokPromoGithubStatus,
  type TikTokPromoCampaignPayload,
} from '@/api/tiktokPromo';

interface FormState {
  title: string;
  tiktokUrl: string;
  imageUrl: string;
  enabled: boolean;
  weight: string;
  oncePerUser: boolean;
  showAfterViews: string;
  trackClicks: boolean;
  startsAt: string;
  endsAt: string;
}

interface ConditionDraft {
  enabled: boolean;
  weight: string;
  oncePerUser: boolean;
  showAfterViews: string;
  trackClicks: boolean;
}

const emptyForm: FormState = {
  title: '',
  tiktokUrl: '',
  imageUrl: '',
  enabled: true,
  weight: '10',
  oncePerUser: true,
  showAfterViews: '0',
  trackClicks: false,
  startsAt: '',
  endsAt: '',
};

const emptyConditionDraft: ConditionDraft = {
  enabled: true,
  weight: '10',
  oncePerUser: true,
  showAfterViews: '0',
  trackClicks: false,
};

function toInputDate(value: string | null): string {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toISOString().slice(0, 16);
}

function fromInputDate(value: string): string | null {
  return value ? new Date(value).toISOString() : null;
}

function numberField(value: string, fallback: number): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function campaignToForm(campaign: TikTokPromoCampaign): FormState {
  return {
    title: campaign.title,
    tiktokUrl: campaign.tiktokUrl,
    imageUrl: campaign.imageUrl,
    enabled: campaign.enabled,
    weight: String(campaign.weight),
    oncePerUser: campaign.oncePerUser,
    showAfterViews: String(campaign.showAfterViews),
    trackClicks: campaign.trackClicks,
    startsAt: toInputDate(campaign.startsAt),
    endsAt: toInputDate(campaign.endsAt),
  };
}

function campaignToConditions(campaign: TikTokPromoCampaign): ConditionDraft {
  return {
    enabled: campaign.enabled,
    weight: String(campaign.weight),
    oncePerUser: campaign.oncePerUser,
    showAfterViews: String(campaign.showAfterViews),
    trackClicks: campaign.trackClicks,
  };
}

function formToPayload(form: FormState): TikTokPromoCampaignPayload {
  return {
    title: form.title.trim(),
    tiktokUrl: form.tiktokUrl.trim(),
    imageUrl: form.imageUrl.trim(),
    enabled: form.enabled,
    weight: numberField(form.weight, 10),
    oncePerUser: form.oncePerUser,
    showAfterViews: numberField(form.showAfterViews, 0),
    trackClicks: form.trackClicks,
    startsAt: fromInputDate(form.startsAt),
    endsAt: fromInputDate(form.endsAt),
  };
}

export default function TikTokPromoAdminPage() {
  const [campaigns, setCampaigns] = useState<TikTokPromoCampaign[]>([]);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [resolvingLink, setResolvingLink] = useState(false);
  const [resettingId, setResettingId] = useState<string | null>(null);
  const [conditionEditingId, setConditionEditingId] = useState<string | null>(null);
  const [conditionSavingId, setConditionSavingId] = useState<string | null>(null);
  const [conditionDraft, setConditionDraft] = useState<ConditionDraft>(emptyConditionDraft);
  const [githubStatus, setGithubStatus] = useState<TikTokPromoGithubStatus | null>(null);
  const [publishingGitHub, setPublishingGitHub] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const preview = useMemo(() => {
    const link = form.tiktokUrl || 'https://www.tiktok.com/@swipa/video/0000000000000000000';
    const imageUrl = form.imageUrl || 'https://swipa.ru/tt/banner.webp';
    return `0\n${link}\n${imageUrl}\n1\n1`;
  }, [form.imageUrl, form.tiktokUrl]);

  async function loadCampaigns() {
    setLoading(true);
    setError(null);
    setNotice(null);
    try {
      const [nextCampaigns, nextGithubStatus] = await Promise.all([
        tiktokPromoApi.listCampaigns(),
        tiktokPromoApi.getGithubStatus(),
      ]);
      setCampaigns(nextCampaigns);
      setGithubStatus(nextGithubStatus);
    } catch (err) {
      setError(extractError(err));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void loadCampaigns();
  }, []);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      const payload = formToPayload(form);
      if (editingId) {
        await tiktokPromoApi.updateCampaign(editingId, payload);
      } else {
        await tiktokPromoApi.createCampaign(payload);
      }
      setForm(emptyForm);
      setEditingId(null);
      await loadCampaigns();
      setNotice('Campaign saved.');
    } catch (err) {
      setError(extractError(err));
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(id: string) {
    setError(null);
    setNotice(null);
    try {
      await tiktokPromoApi.deleteCampaign(id);
      if (editingId === id) {
        setEditingId(null);
        setForm(emptyForm);
      }
      if (conditionEditingId === id) {
        setConditionEditingId(null);
        setConditionDraft(emptyConditionDraft);
      }
      await loadCampaigns();
    } catch (err) {
      setError(extractError(err));
    }
  }

  async function toggleCampaign(campaign: TikTokPromoCampaign) {
    setError(null);
    setNotice(null);
    try {
      await tiktokPromoApi.updateCampaign(campaign.id, { enabled: !campaign.enabled });
      await loadCampaigns();
    } catch (err) {
      setError(extractError(err));
    }
  }

  async function convertTikTokLink() {
    if (!form.tiktokUrl.trim()) return;
    setResolvingLink(true);
    setError(null);
    setNotice(null);
    try {
      const result = await tiktokPromoApi.resolveLink(form.tiktokUrl.trim());
      setForm((prev) => ({ ...prev, tiktokUrl: result.deepLink }));
    } catch (err) {
      setError(extractError(err));
    } finally {
      setResolvingLink(false);
    }
  }

  async function resetCampaignViews(campaign: TikTokPromoCampaign) {
    const confirmed = window.confirm(
      'Reset counters for this campaign? Views and clicks will be cleared, and users who already saw it can receive it again.',
    );
    if (!confirmed) return;
    setResettingId(campaign.id);
    setError(null);
    setNotice(null);
    try {
      const updated = await tiktokPromoApi.resetCampaignViews(campaign.id);
      setCampaigns((prev) => [updated, ...prev.filter((item) => item.id !== campaign.id)]);
      if (editingId === campaign.id) {
        setEditingId(updated.id);
        setForm(campaignToForm(updated));
      }
      if (conditionEditingId === campaign.id) {
        setConditionEditingId(updated.id);
        setConditionDraft(campaignToConditions(updated));
      }
      await loadCampaigns();
      setNotice('Counters reset.');
    } catch (err) {
      setError(extractError(err));
    } finally {
      setResettingId(null);
    }
  }

  function openConditionEditor(campaign: TikTokPromoCampaign) {
    setError(null);
    setNotice(null);
    if (conditionEditingId === campaign.id) {
      setConditionEditingId(null);
      setConditionDraft(emptyConditionDraft);
      return;
    }
    setConditionEditingId(campaign.id);
    setConditionDraft(campaignToConditions(campaign));
  }

  async function saveCampaignConditions(campaign: TikTokPromoCampaign) {
    setConditionSavingId(campaign.id);
    setError(null);
    setNotice(null);
    try {
      const updated = await tiktokPromoApi.updateCampaign(campaign.id, {
        enabled: conditionDraft.enabled,
        weight: numberField(conditionDraft.weight, 10),
        oncePerUser: conditionDraft.oncePerUser,
        showAfterViews: numberField(conditionDraft.showAfterViews, 0),
        trackClicks: conditionDraft.trackClicks,
      });
      if (editingId === campaign.id) {
        setEditingId(updated.id);
        setForm(campaignToForm(updated));
      }
      setConditionEditingId(null);
      setConditionDraft(emptyConditionDraft);
      await loadCampaigns();
      setNotice('Display conditions saved.');
    } catch (err) {
      setError(extractError(err));
    } finally {
      setConditionSavingId(null);
    }
  }

  async function publishGithubConfig() {
    setPublishingGitHub(true);
    setError(null);
    setNotice(null);
    try {
      const status = await tiktokPromoApi.publishGithubConfig();
      setGithubStatus(status);
      setNotice('GitHub config published.');
    } catch (err) {
      setError(extractError(err));
    } finally {
      setPublishingGitHub(false);
    }
  }

  return (
    <div className="min-h-screen bg-surface-950 text-surface-50">
      <header className="border-b border-surface-800 bg-surface-900/80 px-5 py-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-xl font-semibold">TT campaigns</h1>
            <p className="break-all text-sm text-surface-400">
              {githubStatus?.rawUrl ?? 'GitHub config is not configured'}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => void publishGithubConfig()}
              className="btn-secondary h-9 px-3"
              disabled={publishingGitHub || !githubStatus?.configured}
              title={githubStatus?.configured ? 'Publish banner43.conf to GitHub' : 'Configure TT_PROMO_GITHUB_* env first'}
            >
              <RefreshCw size={15} className={clsx(publishingGitHub && 'animate-spin')} />
              Publish
            </button>
            <button
              type="button"
              onClick={() => void loadCampaigns()}
              className="btn-secondary h-9 px-3"
              disabled={loading}
            >
              <RefreshCw size={15} className={clsx(loading && 'animate-spin')} />
              Refresh
            </button>
          </div>
        </div>
        {githubStatus?.lastPublishError && (
          <p className="mt-2 break-all text-xs text-red-300">{githubStatus.lastPublishError}</p>
        )}
        {githubStatus?.lastPublishedAt && !githubStatus.lastPublishError && (
          <p className="mt-2 text-xs text-surface-500">
            Last published {new Date(githubStatus.lastPublishedAt).toLocaleString()}
          </p>
        )}
      </header>

      <main className="grid gap-5 p-5 xl:grid-cols-[minmax(360px,520px),1fr]">
        <section className="rounded-lg border border-surface-800 bg-surface-900 p-4">
          <form className="space-y-4" onSubmit={(event) => void handleSubmit(event)}>
            <div className="flex items-center justify-between gap-3">
              <h2 className="text-sm font-semibold uppercase tracking-wide text-surface-300">
                {editingId ? 'Edit campaign' : 'New campaign'}
              </h2>
              {editingId && (
                <button
                  type="button"
                  className="text-sm text-surface-400 hover:text-surface-100"
                  onClick={() => {
                    setEditingId(null);
                    setForm(emptyForm);
                  }}
                >
                  Reset
                </button>
              )}
            </div>

            {error && (
              <div className="rounded-md border border-red-500/40 bg-red-500/10 px-3 py-2 text-sm text-red-100">
                {error}
              </div>
            )}
            {notice && (
              <div className="rounded-md border border-green-500/40 bg-green-500/10 px-3 py-2 text-sm text-green-100">
                {notice}
              </div>
            )}

            <label className="block space-y-1.5">
              <span className="text-xs font-medium uppercase tracking-wide text-surface-400">Title</span>
              <input
                value={form.title}
                onChange={(event) => setForm((prev) => ({ ...prev, title: event.target.value }))}
                className="irc-input w-full"
                maxLength={120}
                required
              />
            </label>

            <label className="block space-y-1.5">
              <span className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-surface-400">
                <LinkIcon size={13} />
                TikTok URL
              </span>
              <div className="flex gap-2">
                <input
                  value={form.tiktokUrl}
                  onChange={(event) => setForm((prev) => ({ ...prev, tiktokUrl: event.target.value }))}
                  className="irc-input min-w-0 flex-1"
                  placeholder="https://www.tiktok.com/@user/video/..."
                  required
                />
                <button
                  type="button"
                  className="btn-secondary h-10 px-3"
                  title="Convert to app deep link"
                  onClick={() => void convertTikTokLink()}
                  disabled={resolvingLink || !form.tiktokUrl.trim()}
                >
                  <RefreshCw size={15} className={clsx(resolvingLink && 'animate-spin')} />
                </button>
              </div>
            </label>

            <label className="block space-y-1.5">
              <span className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-surface-400">
                <Image size={13} />
                Banner image URL
              </span>
              <input
                value={form.imageUrl}
                onChange={(event) => setForm((prev) => ({ ...prev, imageUrl: event.target.value }))}
                className="irc-input w-full"
                placeholder="https://swipa.ru/tt/banner.webp"
                required
              />
            </label>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <label className="block space-y-1.5">
                <span className="text-xs font-medium uppercase tracking-wide text-surface-400">Starts</span>
                <input
                  type="datetime-local"
                  value={form.startsAt}
                  onChange={(event) => setForm((prev) => ({ ...prev, startsAt: event.target.value }))}
                  className="irc-input w-full"
                />
              </label>
              <label className="block space-y-1.5">
                <span className="text-xs font-medium uppercase tracking-wide text-surface-400">Ends</span>
                <input
                  type="datetime-local"
                  value={form.endsAt}
                  onChange={(event) => setForm((prev) => ({ ...prev, endsAt: event.target.value }))}
                  className="irc-input w-full"
                />
              </label>
            </div>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <label className="block space-y-1.5">
                <span className="text-xs font-medium uppercase tracking-wide text-surface-400">Weight</span>
                <input
                  type="text"
                  inputMode="numeric"
                  value={form.weight}
                  onChange={(event) => setForm((prev) => ({ ...prev, weight: event.target.value }))}
                  className="irc-input w-full"
                />
                <span className="text-[11px] text-surface-500">Random priority if several campaigns are active.</span>
              </label>
              <label className="block space-y-1.5">
                <span className="text-xs font-medium uppercase tracking-wide text-surface-400">Views</span>
                <input
                  type="text"
                  inputMode="numeric"
                  value={form.showAfterViews}
                  onChange={(event) => setForm((prev) => ({ ...prev, showAfterViews: event.target.value }))}
                  className="irc-input w-full"
                />
                <span className="text-[11px] text-surface-500">Show after this many videos.</span>
              </label>
            </div>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <button
                type="button"
                onClick={() => setForm((prev) => ({ ...prev, enabled: !prev.enabled }))}
                className={clsx('btn-secondary h-10 self-end justify-center', form.enabled && 'text-green-300')}
              >
                {form.enabled ? <ToggleRight size={17} /> : <ToggleLeft size={17} />}
                Enabled
              </button>
              <button
                type="button"
                onClick={() => setForm((prev) => ({ ...prev, oncePerUser: !prev.oncePerUser }))}
                className={clsx('btn-secondary h-10 self-end justify-center', form.oncePerUser && 'text-brand-300')}
              >
                {form.oncePerUser ? <ToggleRight size={17} /> : <ToggleLeft size={17} />}
                Once
              </button>
              <button
                type="button"
                onClick={() => setForm((prev) => ({ ...prev, trackClicks: !prev.trackClicks }))}
                className={clsx('btn-secondary h-10 self-end justify-center', form.trackClicks && 'text-amber-300')}
              >
                {form.trackClicks ? <ToggleRight size={17} /> : <ToggleLeft size={17} />}
                Clicks
              </button>
            </div>

            <pre className="max-h-36 overflow-auto rounded-md bg-surface-950 p-3 text-xs text-surface-300">
              {preview}
            </pre>

            <button type="submit" className="btn-primary h-10 w-full justify-center" disabled={saving}>
              {editingId ? <Save size={16} /> : <Plus size={16} />}
              {saving ? 'Saving...' : editingId ? 'Save campaign' : 'Create campaign'}
            </button>
          </form>
        </section>

        <section className="space-y-3">
          {campaigns.map((campaign) => (
            <article
              key={campaign.id}
              className="rounded-lg border border-surface-800 bg-surface-900 p-4"
            >
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="truncate text-base font-semibold">{campaign.title}</h3>
                    <span
                      className={clsx(
                        'rounded-full px-2 py-0.5 text-xs',
                        campaign.enabled ? 'bg-green-500/15 text-green-300' : 'bg-surface-800 text-surface-400',
                      )}
                    >
                      {campaign.enabled ? 'active' : 'paused'}
                    </span>
                    {campaign.oncePerUser && (
                      <span className="rounded-full bg-brand-500/15 px-2 py-0.5 text-xs text-brand-300">
                        once
                      </span>
                    )}
                  </div>
                  <p className="mt-1 text-sm text-surface-400">
                    weight {campaign.weight} · after {campaign.showAfterViews} views
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <a
                    href={campaign.tiktokUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="btn-secondary h-9 px-3"
                  >
                    <ExternalLink size={15} />
                  </a>
                  <button
                    type="button"
                    className="btn-secondary h-9 px-3"
                    onClick={() => void toggleCampaign(campaign)}
                  >
                    {campaign.enabled ? <ToggleRight size={16} /> : <ToggleLeft size={16} />}
                  </button>
                  <button
                    type="button"
                    className="btn-secondary h-9 px-3"
                    title="Edit display conditions"
                    onClick={() => openConditionEditor(campaign)}
                  >
                    <SlidersHorizontal size={15} />
                  </button>
                  <button
                    type="button"
                    className="btn-secondary h-9 gap-2 px-3"
                    title="Reset views and clicks"
                    onClick={() => void resetCampaignViews(campaign)}
                    disabled={resettingId === campaign.id}
                  >
                    <RotateCcw size={15} className={clsx(resettingId === campaign.id && 'animate-spin')} />
                    <span className="text-xs">Reset</span>
                  </button>
                  <button
                    type="button"
                    className="btn-secondary h-9 px-3"
                    onClick={() => {
                      setEditingId(campaign.id);
                      setForm(campaignToForm(campaign));
                    }}
                  >
                    <Pencil size={15} />
                  </button>
                  <button
                    type="button"
                    className="btn-secondary h-9 px-3 text-red-300"
                    onClick={() => void handleDelete(campaign.id)}
                  >
                    <Trash2 size={15} />
                  </button>
                </div>
              </div>
              <div className="mt-3 grid gap-3 lg:grid-cols-[180px,1fr]">
                <img
                  src={campaign.imageUrl}
                  alt=""
                  className="aspect-video w-full rounded-md border border-surface-800 object-cover"
                />
                <div className="min-w-0 space-y-2 text-sm text-surface-300">
                  <p className="truncate">{campaign.tiktokUrl}</p>
                  <p className="truncate text-surface-500">{campaign.imageUrl}</p>
                  <p className="text-surface-500">
                    impressions {campaign.stats.impressions} · clicks {campaign.stats.clicks}
                    {campaign.stats.lastClickAt ? ` · last click ${new Date(campaign.stats.lastClickAt).toLocaleString()}` : ''}
                  </p>
                  <p className="text-surface-500">
                    {campaign.startsAt ? new Date(campaign.startsAt).toLocaleString() : 'No start'} ·{' '}
                    {campaign.endsAt ? new Date(campaign.endsAt).toLocaleString() : 'No end'}
                  </p>
                </div>
              </div>
              {conditionEditingId === campaign.id && (
                <div className="mt-4 rounded-md border border-surface-800 bg-surface-950 p-3">
                  <div className="mb-3 flex items-center justify-between gap-3">
                    <h4 className="text-xs font-semibold uppercase tracking-wide text-surface-400">
                      Display conditions
                    </h4>
                    <button
                      type="button"
                      className="text-surface-500 hover:text-surface-200"
                      onClick={() => {
                        setConditionEditingId(null);
                        setConditionDraft(emptyConditionDraft);
                      }}
                    >
                      <X size={15} />
                    </button>
                  </div>
                  <div className="grid gap-3 lg:grid-cols-[minmax(170px,1.2fr),minmax(170px,1.2fr),repeat(3,minmax(0,1fr))]">
                    <label className="block space-y-1.5">
                      <span className="text-xs font-medium uppercase tracking-wide text-surface-500">Views</span>
                      <input
                        type="text"
                        inputMode="numeric"
                        value={conditionDraft.showAfterViews}
                        onChange={(event) => setConditionDraft((prev) => ({
                          ...prev,
                          showAfterViews: event.target.value,
                        }))}
                        className="irc-input w-full"
                      />
                      <div className="flex gap-1">
                        {['0', '3', '5'].map((value) => (
                          <button
                            key={value}
                            type="button"
                            className={clsx(
                              'h-7 rounded-md border px-2 text-xs transition-colors',
                              conditionDraft.showAfterViews === value
                                ? 'border-brand-500 bg-brand-500/20 text-brand-100'
                                : 'border-surface-700 bg-surface-900 text-surface-400 hover:text-surface-100',
                            )}
                            onClick={() => setConditionDraft((prev) => ({ ...prev, showAfterViews: value }))}
                          >
                            {value}
                          </button>
                        ))}
                      </div>
                      <span className="text-[11px] text-surface-500">Show after this many videos.</span>
                    </label>
                    <label className="block space-y-1.5">
                      <span className="text-xs font-medium uppercase tracking-wide text-surface-500">Priority</span>
                      <input
                        type="text"
                        inputMode="numeric"
                        value={conditionDraft.weight}
                        onChange={(event) => setConditionDraft((prev) => ({
                          ...prev,
                          weight: event.target.value,
                        }))}
                        className="irc-input w-full"
                      />
                      <span className="text-[11px] text-surface-500">Random priority if several campaigns are active.</span>
                    </label>
                    <button
                      type="button"
                      onClick={() => setConditionDraft((prev) => ({ ...prev, enabled: !prev.enabled }))}
                      className={clsx('btn-secondary h-10 self-end justify-center', conditionDraft.enabled && 'text-green-300')}
                    >
                      {conditionDraft.enabled ? <ToggleRight size={17} /> : <ToggleLeft size={17} />}
                      Enabled
                    </button>
                    <button
                      type="button"
                      onClick={() => setConditionDraft((prev) => ({ ...prev, oncePerUser: !prev.oncePerUser }))}
                      className={clsx('btn-secondary h-10 self-end justify-center', conditionDraft.oncePerUser && 'text-brand-300')}
                    >
                      {conditionDraft.oncePerUser ? <ToggleRight size={17} /> : <ToggleLeft size={17} />}
                      Once
                    </button>
                    <button
                      type="button"
                      onClick={() => setConditionDraft((prev) => ({ ...prev, trackClicks: !prev.trackClicks }))}
                      className={clsx('btn-secondary h-10 self-end justify-center', conditionDraft.trackClicks && 'text-amber-300')}
                    >
                      {conditionDraft.trackClicks ? <ToggleRight size={17} /> : <ToggleLeft size={17} />}
                      Clicks
                    </button>
                  </div>
                  <div className="mt-3 flex justify-end">
                    <button
                      type="button"
                      className="btn-primary h-10 px-4"
                      onClick={() => void saveCampaignConditions(campaign)}
                      disabled={conditionSavingId === campaign.id}
                    >
                      <Save size={15} />
                      {conditionSavingId === campaign.id ? 'Saving...' : 'Save conditions'}
                    </button>
                  </div>
                </div>
              )}
            </article>
          ))}

          {!loading && campaigns.length === 0 && (
            <div className="rounded-lg border border-dashed border-surface-700 p-8 text-center text-sm text-surface-400">
              No campaigns
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
